export type BrowserCaptureSpec = {
  schema_version?: 'browser_capture_spec.v1';
  schema_ref: { schema_id: string; version: number };
  page_match: { url_contains?: string | null; title_contains?: string | null };
  frame_match?: {
    url_contains?: string | null;
    name?: string | null;
    element_id?: string | null;
  } | null;
  allowed_origins: string[];
  root_selector: string;
  row_selector: string;
  fields: Array<{
    name: string;
    selector: string;
    source?: 'text' | 'attribute';
    attribute?: string | null;
    nullable?: boolean;
    max_length?: number;
  }>;
  root_fields?: BrowserCaptureSpec['fields'];
  min_rows?: number;
  max_rows?: number;
  max_bytes?: number;
  timeout_ms?: number;
};

// The backend owns selector, schema and resource validation. Keep this editor
// check structural so saving a flow does not duplicate that contract.
export const isBrowserCaptureSpec = (value: unknown): value is BrowserCaptureSpec => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return false;
  const spec = value as Record<string, unknown>;
  return (
    !!spec.schema_ref &&
    typeof spec.schema_ref === 'object' &&
    !Array.isArray(spec.schema_ref) &&
    !!spec.page_match &&
    typeof spec.page_match === 'object' &&
    !Array.isArray(spec.page_match) &&
    Array.isArray(spec.allowed_origins) &&
    spec.allowed_origins.length > 0 &&
    typeof spec.root_selector === 'string' &&
    typeof spec.row_selector === 'string' &&
    Array.isArray(spec.fields) &&
    spec.fields.length > 0
  );
};

export const createDefaultBrowserExtractionStage = (
  stages: BrowserFlowStageDefinition[],
): BrowserFlowStageDefinition => {
  const stage = createNextDefaultBrowserFlowStage(stages);
  return {
    ...stage,
    label: 'Structured Extraction',
    stage_kind: 'structured_extraction',
    executor: 'deterministic_cdp',
    instruction_text: 'Capture the reviewed DOM projection from the current page.',
    extraction: {
      schema_version: 'browser_capture_spec.v1',
      schema_ref: { schema_id: 'replace_with_capture_schema', version: 1 },
      page_match: { url_contains: 'https://example.com/' },
      allowed_origins: ['https://example.com'],
      root_selector: '#replace-with-reviewed-root',
      row_selector: '.row',
      fields: [{ name: 'value', selector: '.value', source: 'text', nullable: false }],
    },
  };
};

export type BrowserFlowStageKind =
  | 'narrative'
  | 'verification'
  | 'structured_extraction'
  | 'deterministic_interaction'
  | 'action'
  | 'post_action_observation';

export type BrowserStageOutcomeField = {
  name: string;
  value_type: 'boolean' | 'integer' | 'string' | 'enum';
  required?: boolean;
  enum_values?: string[];
  minimum?: number;
  maximum?: number;
};

export type BrowserStageOutcomeContract = {
  schema_version: 'browser_stage_outcome_contract.v2';
  fields: BrowserStageOutcomeField[];
};

export type BrowserStageReadinessCondition = {
  field_name: string;
  allowed_values: Array<boolean | number | string>;
};

export type BrowserStageReadinessPolicy = {
  schema_version: 'browser_stage_readiness_policy.v1';
  conditions: BrowserStageReadinessCondition[];
};

export type BrowserFlowStageDefinition = {
  stage_id: string;
  label: string;
  instruction_text: string;
  stage_kind: BrowserFlowStageKind;
  outcome_contract: BrowserStageOutcomeContract;
  completion_criteria?: string | null;
  expected_outcome?: string | null;
  may_require_user_intervention?: boolean;
  executor?: 'browser_use_agent' | 'deterministic_cdp';
  executor_config?: Record<string, unknown>;
  extraction?: BrowserCaptureSpec | null;
  result_completion?: Record<string, unknown> | null;
  readiness_policy?: BrowserStageReadinessPolicy | null;
  metadata?: Record<string, unknown>;
};

export type BrowserMultiStageFlowDefinition = {
  schema_version: 'browser_multi_stage_flow.v2';
  stages: BrowserFlowStageDefinition[];
};

export type BrowserFlowAuthoringSpec = {
  schema_version: 'browser_flow_authoring_spec.v1';
  flow_schema_version: 'browser_multi_stage_flow.v2';
  outcome_contract_schema_version: 'browser_stage_outcome_contract.v2';
  stage_kinds: Partial<
    Record<
      BrowserFlowStageKind,
      {
        platform_owned_outcome_fields?: string[];
        requires_boolean_required_outcome_fields_with_matching_operation_result_keys?: boolean;
        requires_later_flow_stage?: boolean;
        result_completion_allowed_executors?: Array<'browser_use_agent' | 'deterministic_cdp'>;
      }
    >
  >;
};

export type BrowserFlowDefinitionParseResult = {
  value: BrowserMultiStageFlowDefinition | null;
  error: string | null;
};

export const parseBrowserMultiStageFlowDefinition = (value: unknown): BrowserFlowDefinitionParseResult => {
  if (value == null || (typeof value === 'string' && value.trim() === '')) {
    return { value: null, error: 'Missing multi-stage flow definition.' };
  }

  try {
    const parsed = typeof value === 'string' ? (JSON.parse(value) as unknown) : value;
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
      return { value: null, error: 'Flow definition must be a JSON object.' };
    }

    const record = parsed as Record<string, unknown>;
    if (
      record.schema_version !== 'browser_multi_stage_flow.v2' ||
      !Array.isArray(record.stages) ||
      Object.keys(record).some((key) => key !== 'schema_version' && key !== 'stages')
    ) {
      return { value: null, error: 'Flow definition is missing a valid schema_version or stages array.' };
    }

    const stages = record.stages;
    if (!stages.every((stage) => !!stage && typeof stage === 'object' && isValidBrowserFlowStageDefinition(stage))) {
      return { value: null, error: 'Some stages could not be parsed.' };
    }
    const typedStages = stages as BrowserFlowStageDefinition[];
    const semanticError = validateBrowserFlowSemantics(typedStages);
    return {
      value: semanticError ? null : (parsed as BrowserMultiStageFlowDefinition),
      error: semanticError,
    };
  } catch {
    return { value: null, error: 'Flow definition contains invalid JSON.' };
  }
};

export const serializeBrowserMultiStageFlowDefinition = (
  definition: BrowserMultiStageFlowDefinition,
): string => JSON.stringify(definition, null, 2);

export const createDefaultBrowserFlowStage = (index: number): BrowserFlowStageDefinition => ({
  stage_id: `stage_${index + 1}`,
  label: `Stage ${index + 1}`,
  instruction_text: 'Inspect the current page and report the requested outcome.',
  stage_kind: 'narrative',
  outcome_contract: { schema_version: 'browser_stage_outcome_contract.v2', fields: [] },
  completion_criteria: '',
  expected_outcome: '',
  may_require_user_intervention: false,
  metadata: {},
});

export const createNextDefaultBrowserFlowStage = (
  stages: BrowserFlowStageDefinition[],
): BrowserFlowStageDefinition => {
  const existingStageIds = new Set(stages.map((stage) => stage.stage_id));
  let index = stages.length;
  let candidateStage = createDefaultBrowserFlowStage(index);
  while (existingStageIds.has(candidateStage.stage_id)) {
    index += 1;
    candidateStage = createDefaultBrowserFlowStage(index);
  }
  return candidateStage;
};

export const createDefaultBrowserActionStages = (
  stages: BrowserFlowStageDefinition[],
): [BrowserFlowStageDefinition, BrowserFlowStageDefinition] => {
  const existingStageIds = new Set(stages.map((stage) => stage.stage_id));
  let actionSeed = createNextDefaultBrowserFlowStage(stages);
  while (
    existingStageIds.has(`${actionSeed.stage_id}_action`) ||
    existingStageIds.has(`${actionSeed.stage_id}_observation`)
  ) {
    actionSeed = createNextDefaultBrowserFlowStage([...stages, actionSeed]);
  }
  return [
    {
      ...actionSeed,
      stage_id: `${actionSeed.stage_id}_action`,
      label: 'Submit Action',
      stage_kind: 'action',
      executor: 'deterministic_cdp',
      executor_config: {
        plan_version: 'deterministic_browser_plan.v1',
        control_plan_id: 'operation_sequence',
        page_match: { url_contains: '/replace-with-reviewed-checkout-path' },
        operations: [
          {
            kind: 'click',
            target: { kind: 'selector', value: '#replace-with-reviewed-selector' },
            result_key: 'submit_attempted',
          },
        ],
      },
      outcome_contract: {
        schema_version: 'browser_stage_outcome_contract.v2',
        fields: [{ name: 'submit_attempted', value_type: 'boolean', required: true }],
      },
    },
    {
      ...actionSeed,
      stage_id: `${actionSeed.stage_id}_observation`,
      label: 'Post-action Observation',
      instruction_text: 'Observe the current page only. Do not repeat the preceding action.',
      stage_kind: 'post_action_observation',
      executor: 'browser_use_agent',
      outcome_contract: { schema_version: 'browser_stage_outcome_contract.v2', fields: [] },
    },
  ];
};

export const createDefaultBrowserInteractionStage = (
  stages: BrowserFlowStageDefinition[],
): BrowserFlowStageDefinition => {
  const existingStageIds = new Set(stages.map((stage) => stage.stage_id));
  let stage = createNextDefaultBrowserFlowStage(stages);
  while (existingStageIds.has(`${stage.stage_id}_interaction`)) {
    stage = createNextDefaultBrowserFlowStage([...stages, stage]);
  }
  return {
    ...stage,
    stage_id: `${stage.stage_id}_interaction`,
    label: 'Deterministic Interaction',
    instruction_text: 'Attach to the carried browser session and perform the reviewed pre-submit interaction.',
    stage_kind: 'deterministic_interaction',
    executor: 'deterministic_cdp',
    executor_config: {
      plan_version: 'deterministic_browser_plan.v1',
      control_plan_id: 'attach_probe',
      operations: [],
    },
  };
};

export const withStateObservation = (stage: BrowserFlowStageDefinition): BrowserFlowStageDefinition => ({
  ...stage,
  stage_kind: 'verification',
  executor: 'deterministic_cdp',
  executor_config: {
    plan_version: 'deterministic_browser_plan.v1',
    control_plan_id: 'state_observation',
    page_match: { url_contains: '/replace-with-reviewed-path' },
    operations: [],
    state_observation: {
      schema_version: 'browser_state_observation.v1',
      allowed_origins: ['https://example.com'],
      root_selector: '#replace-with-reviewed-root',
      timeout_ms: 45000,
      conditions: [
        {
          id: 'destination_visible',
          kind: 'visible_count',
          selector: '#replace-with-reviewed-marker',
          min_count: 1,
          max_count: 1,
        },
      ],
      blockers: [],
    },
  },
  outcome_contract: {
    schema_version: 'browser_stage_outcome_contract.v2',
    fields: [
      {
        name: 'state_status',
        value_type: 'enum',
        required: true,
        enum_values: ['ready', 'not_ready', 'blocked', 'unknown'],
      },
    ],
  },
  readiness_policy: {
    schema_version: 'browser_stage_readiness_policy.v1',
    conditions: [{ field_name: 'state_status', allowed_values: ['ready'] }],
  },
  result_completion: null,
});

export const createDefaultBrowserInteractionStages = (
  stages: BrowserFlowStageDefinition[],
): [BrowserFlowStageDefinition, BrowserFlowStageDefinition] => {
  const interaction = createDefaultBrowserInteractionStage(stages);
  const followUp = createNextDefaultBrowserFlowStage([...stages, interaction]);
  return [
    interaction,
    {
      ...followUp,
      label: 'Verify Pre-submit State',
      instruction_text: 'Verify the reviewed pre-submit state before any final action.',
      stage_kind: 'verification',
      outcome_contract: {
        schema_version: 'browser_stage_outcome_contract.v2',
        fields: [{ name: 'ready', value_type: 'boolean', required: true }],
      },
    },
  ];
};

export const parseBrowserFlowAuthoringSpec = (value: unknown): BrowserFlowAuthoringSpec | null => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return null;
  }
  const record = value as Record<string, unknown>;
  if (
    record.schema_version !== 'browser_flow_authoring_spec.v1' ||
    record.flow_schema_version !== 'browser_multi_stage_flow.v2' ||
    record.outcome_contract_schema_version !== 'browser_stage_outcome_contract.v2' ||
    !isBrowserFlowStageKinds(record.stage_kinds)
  ) {
    return null;
  }
  return record as BrowserFlowAuthoringSpec;
};

const isValidBrowserFlowStageDefinition = (stage: object): stage is BrowserFlowStageDefinition => {
  const record = stage as Record<string, unknown>;
  const allowedKeys = new Set([
    'stage_id',
    'label',
    'instruction_text',
    'stage_kind',
    'outcome_contract',
    'completion_criteria',
    'expected_outcome',
    'may_require_user_intervention',
    'executor',
    'executor_config',
    'extraction',
    'result_completion',
    'readiness_policy',
    'metadata',
  ]);
  const executorIsValid =
    record.executor === undefined ||
    record.executor === 'browser_use_agent' ||
    record.executor === 'deterministic_cdp';
  const deterministicStageUsesDeterministicCdp =
    (record.stage_kind !== 'action' && record.stage_kind !== 'deterministic_interaction') ||
    (record.executor === 'deterministic_cdp' && isDeterministicBrowserPlan(record.executor_config));
  const otherStageUsesBrowserAgent =
    record.stage_kind === 'action' ||
    record.stage_kind === 'deterministic_interaction' ||
    record.stage_kind === 'structured_extraction' ||
    (record.stage_kind === 'verification' && record.executor === 'deterministic_cdp' &&
      isDeterministicBrowserPlan(record.executor_config) &&
      (record.executor_config as Record<string, unknown>).control_plan_id === 'state_observation') ||
    record.executor === undefined ||
    record.executor === 'browser_use_agent';
  const observerUsesBrowserAgent =
    record.stage_kind !== 'post_action_observation' ||
    record.executor === undefined ||
    record.executor === 'browser_use_agent';
  return (
    Object.keys(record).every((key) => allowedKeys.has(key)) &&
    typeof record.stage_id === 'string' &&
    record.stage_id.trim().length > 0 &&
    typeof record.label === 'string' &&
    record.label.trim().length > 0 &&
    typeof record.instruction_text === 'string' &&
    record.instruction_text.trim().length > 0 &&
    isStageKind(record.stage_kind) &&
    isOutcomeContract(record.outcome_contract) &&
    isOptionalString(record.completion_criteria) &&
    isOptionalString(record.expected_outcome) &&
    (record.may_require_user_intervention === undefined ||
      typeof record.may_require_user_intervention === 'boolean') &&
    executorIsValid &&
    (record.stage_kind === 'structured_extraction'
      ? record.executor === 'deterministic_cdp' &&
        isBrowserCaptureSpec(record.extraction) &&
        (!record.executor_config || Object.keys(record.executor_config as object).length === 0)
      : record.extraction == null) &&
    isOptionalRecord(record.executor_config) &&
    deterministicStageUsesDeterministicCdp &&
    otherStageUsesBrowserAgent &&
    observerUsesBrowserAgent &&
    isOptionalRecord(record.result_completion) &&
    isOptionalReadinessPolicy(record.readiness_policy) &&
    isOptionalRecord(record.metadata)
  );
};

const isStageKind = (value: unknown): value is BrowserFlowStageKind =>
  value === 'narrative' ||
  value === 'verification' ||
  value === 'structured_extraction' ||
  value === 'deterministic_interaction' ||
  value === 'action' ||
  value === 'post_action_observation';

const isOutcomeContract = (value: unknown): value is BrowserStageOutcomeContract => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return false;
  }
  const record = value as Record<string, unknown>;
  return (
    Object.keys(record).every((key) => key === 'schema_version' || key === 'fields') &&
    record.schema_version === 'browser_stage_outcome_contract.v2' &&
    Array.isArray(record.fields) &&
    record.fields.every(isOutcomeField)
  );
};

const isOutcomeField = (value: unknown): value is BrowserStageOutcomeField => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return false;
  }
  const record = value as Record<string, unknown>;
  const allowedKeys = new Set(['name', 'value_type', 'required', 'enum_values', 'minimum', 'maximum']);
  return (
    Object.keys(record).every((key) => allowedKeys.has(key)) &&
    typeof record.name === 'string' &&
    (record.value_type === 'boolean' ||
      record.value_type === 'integer' ||
      record.value_type === 'string' ||
      record.value_type === 'enum') &&
    (record.required === undefined || typeof record.required === 'boolean') &&
    (record.enum_values === undefined ||
      (Array.isArray(record.enum_values) && record.enum_values.every((entry) => typeof entry === 'string'))) &&
    (record.minimum === undefined || (Number.isInteger(record.minimum) && Number.isFinite(record.minimum))) &&
    (record.maximum === undefined || (Number.isInteger(record.maximum) && Number.isFinite(record.maximum)))
  );
};

const isOptionalString = (value: unknown): boolean =>
  value === undefined || value === null || typeof value === 'string';

const isOptionalRecord = (value: unknown): boolean =>
  value === undefined || value === null || (typeof value === 'object' && !Array.isArray(value));

const isOptionalReadinessPolicy = (value: unknown): value is BrowserStageReadinessPolicy | null | undefined => {
  if (value === undefined || value === null) {
    return true;
  }
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return false;
  }
  const record = value as Record<string, unknown>;
  return (
    Object.keys(record).every((key) => key === 'schema_version' || key === 'conditions') &&
    record.schema_version === 'browser_stage_readiness_policy.v1' &&
    Array.isArray(record.conditions) &&
    record.conditions.every(isReadinessCondition)
  );
};

const isReadinessCondition = (value: unknown): boolean => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return false;
  }
  const condition = value as Record<string, unknown>;
  const allowedValues = condition.allowed_values;
  return (
    Object.keys(condition).every((key) => key === 'field_name' || key === 'allowed_values') &&
    typeof condition.field_name === 'string' &&
    Array.isArray(allowedValues) &&
    allowedValues.every(
      (allowedValue: unknown) =>
        typeof allowedValue === 'boolean' ||
        (typeof allowedValue === 'number' && Number.isInteger(allowedValue) && Number.isFinite(allowedValue)) ||
        typeof allowedValue === 'string',
    )
  );
};

const isBrowserFlowStageKinds = (value: unknown): value is BrowserFlowAuthoringSpec['stage_kinds'] => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return false;
  }
  const stageKinds = value as Record<string, unknown>;
  const requiredKinds: BrowserFlowStageKind[] = [
    'narrative',
    'verification',
    'deterministic_interaction',
    'action',
    'post_action_observation',
  ];
  if (
    !requiredKinds.every(
      (kind) =>
        stageKinds[kind] !== null &&
        typeof stageKinds[kind] === 'object' &&
        !Array.isArray(stageKinds[kind]),
    )
  ) {
    return false;
  }
  const observer = stageKinds.post_action_observation as Record<string, unknown>;
  return (
    observer.platform_owned_outcome_fields === undefined ||
    (Array.isArray(observer.platform_owned_outcome_fields) &&
      observer.platform_owned_outcome_fields.every((field) => typeof field === 'string'))
  );
};

const MUTATING_OPERATION_KINDS = new Set(['click', 'fill', 'press', 'check', 'uncheck', 'select_option']);
const DETERMINISTIC_INTERACTION_RESULT_KINDS = new Set([...MUTATING_OPERATION_KINDS, 'assert_value_matches']);
const PLATFORM_OBSERVER_FIELD_NAMES = new Set([
  'post_action_observation_complete',
  'confirmation_present',
  'processing_present',
  'post_action_target_state',
]);

export const isDeterministicBrowserPlan = (value: unknown): value is Record<string, unknown> => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return false;
  }
  const plan = value as Record<string, unknown>;
  if (
    plan.plan_version !== 'deterministic_browser_plan.v1' ||
    typeof plan.control_plan_id !== 'string' ||
    !['attach_probe', 'operation_sequence', 'state_observation'].includes(plan.control_plan_id) ||
    !Array.isArray(plan.operations) ||
    (plan.page_match != null && !isDeterministicPageMatch(plan.page_match))
  ) {
    return false;
  }
  if (plan.control_plan_id === 'state_observation') {
    if (
      Object.keys(plan).some(
        (key) =>
          ![
            'plan_version',
            'control_plan_id',
            'attach_timeout_seconds',
            'page_match',
            'frame_match',
            'operations',
            'state_observation',
          ].includes(key),
      ) ||
      (plan.attach_timeout_seconds !== undefined &&
        (typeof plan.attach_timeout_seconds !== 'number' ||
          !Number.isFinite(plan.attach_timeout_seconds) ||
          plan.attach_timeout_seconds <= 0)) ||
      (plan.frame_match != null && !isDeterministicFrameMatch(plan.frame_match))
    )
      return false;
    return (
      plan.operations.length === 0 && !!plan.page_match && isStateObservationSpec(plan.state_observation)
    );
  }
  if (plan.state_observation != null) return false;
  if (plan.control_plan_id === 'attach_probe') {
    return plan.operations.length === 0;
  }
  return plan.operations.length > 0 && plan.operations.every(isDeterministicBrowserOperation);
};

export const isStateObservationSpec = (value: unknown): boolean => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return false;
  const spec = value as Record<string, unknown>;
  const bounded = (x: unknown, max: number) =>
    typeof x === 'string' && x.trim().length > 0 && x.length <= max;
  if (
    Object.keys(spec).some(
      (k) =>
        ![
          'schema_version',
          'allowed_origins',
          'root_selector',
          'timeout_ms',
          'conditions',
          'blockers',
        ].includes(k),
    ) ||
    (spec.schema_version !== undefined && spec.schema_version !== 'browser_state_observation.v1') ||
    !bounded(spec.root_selector, 512) ||
    !Array.isArray(spec.allowed_origins) ||
    spec.allowed_origins.length < 1 ||
    spec.allowed_origins.length > 16 ||
    !spec.allowed_origins.every((origin) => {
      try {
        const url = new URL(String(origin));
        return (
          typeof origin === 'string' &&
          origin.length <= 2048 &&
          ['http:', 'https:'].includes(url.protocol) &&
          url.origin === origin
        );
      } catch {
        return false;
      }
    }) ||
    (spec.timeout_ms !== undefined &&
      (!Number.isInteger(spec.timeout_ms) ||
        Number(spec.timeout_ms) < 100 ||
        Number(spec.timeout_ms) > 60000)) ||
    !Array.isArray(spec.conditions) ||
    spec.conditions.length < 1 ||
    spec.conditions.length > 32 ||
    (spec.blockers !== undefined && (!Array.isArray(spec.blockers) || spec.blockers.length > 16))
  )
    return false;
  const ids = new Set();
  for (const raw of [...spec.conditions, ...((spec.blockers ?? []) as unknown[])]) {
    if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return false;
    const c = raw as Record<string, unknown>;
    if (
      Object.keys(c).some(
        (k) =>
          ![
            'id',
            'kind',
            'selector',
            'expected',
            'attribute',
            'whitespace',
            'min_count',
            'max_count',
          ].includes(k),
      ) ||
      typeof c.id !== 'string' ||
      !/^[A-Za-z][A-Za-z0-9_]{0,63}$/.test(c.id) ||
      ids.has(c.id) ||
      !bounded(c.selector, 512)
    )
      return false;
    ids.add(c.id);
    const min = c.min_count === undefined ? 1 : c.min_count;
    const max = c.max_count === undefined ? 1 : c.max_count;
    if (
      !Number.isInteger(min) ||
      !Number.isInteger(max) ||
      Number(min) < 0 ||
      Number(max) > 1000 ||
      Number(min) > Number(max)
    )
      return false;
    if (c.kind === 'visible_count') {
      if (
        c.expected != null ||
        c.attribute != null ||
        (c.whitespace !== undefined && c.whitespace !== 'exact')
      )
        return false;
    } else {
      if (
        typeof c.kind !== 'string' ||
        !['text_equals', 'attribute_equals'].includes(c.kind) ||
        min !== 1 ||
        max !== 1 ||
        !c.expected ||
        typeof c.expected !== 'object' ||
        Array.isArray(c.expected)
      )
        return false;
      const expected = c.expected as Record<string, unknown>;
      if (Object.keys(expected).some((k) => !['literal', 'context_pointer'].includes(k))) return false;
      const literal = expected.literal != null,
        pointer = expected.context_pointer != null;
      if (
        literal === pointer ||
        (literal && (typeof expected.literal !== 'string' || expected.literal.length > 4096)) ||
        (pointer &&
          (!bounded(expected.context_pointer, 512) ||
            !String(expected.context_pointer).startsWith('/') ||
            /~(?![01])/.test(String(expected.context_pointer))))
      )
        return false;
      if (c.whitespace !== undefined &&
          (typeof c.whitespace !== 'string' || !['exact', 'collapse_whitespace'].includes(c.whitespace)))
        return false;
      if (
        c.kind === 'attribute_equals'
          ? typeof c.attribute !== 'string' ||
            !['aria-label', 'aria-current', 'aria-selected', 'datetime', 'data-state'].includes(c.attribute)
          : c.attribute != null
      )
        return false;
    }
  }
  return new TextEncoder().encode(JSON.stringify(spec)).length <= 65536;
};

const isDeterministicPageMatch = (value: unknown): boolean => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return false;
  const match = value as Record<string, unknown>;
  return (
    Object.keys(match).every((key) => key === 'url_contains' || key === 'title_contains') &&
    [match.url_contains, match.title_contains].every(
      (entry) => entry == null || typeof entry === 'string',
    ) &&
    [match.url_contains, match.title_contains].some(
      (entry) => typeof entry === 'string' && entry.trim().length > 0,
    )
  );
};

const isDeterministicFrameMatch = (value: unknown): boolean => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return false;
  const match = value as Record<string, unknown>;
  const keys = ['url_contains', 'name', 'element_id'];
  return (
    Object.keys(match).every((key) => keys.includes(key)) &&
    keys.every((key) => match[key] == null || typeof match[key] === 'string') &&
    keys.some((key) => typeof match[key] === 'string' && match[key].trim().length > 0)
  );
};

const isDeterministicBrowserOperation = (value: unknown): boolean => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return false;
  }
  const operation = value as Record<string, unknown>;
  const target = operation.target;
  if (
    typeof operation.kind !== 'string' ||
    ![
      'wait_for_target',
      'click',
      'fill',
      'press',
      'check',
      'uncheck',
      'select_option',
      'assert_value_matches',
    ].includes(operation.kind) ||
    !target ||
    typeof target !== 'object' ||
    Array.isArray(target)
  ) {
    return false;
  }
  const targetRecord = target as Record<string, unknown>;
  return (
    Object.keys(targetRecord).every((key) => ['kind', 'value', 'exact', 'role'].includes(key)) &&
    (targetRecord.exact === undefined || typeof targetRecord.exact === 'boolean') &&
    (targetRecord.kind === 'text' ||
      targetRecord.kind === 'selector' ||
      (targetRecord.kind === 'role' &&
        typeof targetRecord.role === 'string' &&
        ['button', 'link', 'checkbox', 'radio', 'textbox', 'combobox', 'menuitem'].includes(targetRecord.role) &&
        targetRecord.exact === true)) &&
    (targetRecord.kind === 'role' ||
      targetRecord.role === undefined ||
      targetRecord.role === null) &&
    (targetRecord.kind !== 'selector' || targetRecord.exact !== true) &&
    typeof targetRecord.value === 'string' &&
    targetRecord.value.trim().length > 0 &&
    (operation.result_key === undefined || typeof operation.result_key === 'string')
  );
};

const validateBrowserFlowSemantics = (stages: BrowserFlowStageDefinition[]): string | null => {
  if (stages.length === 0) {
    return 'Flow definition requires at least one stage.';
  }
  const stageIds = new Set<string>();
  for (const stage of stages) {
    if (stage.executor_config?.control_plan_id === 'state_observation' &&
        (stage.stage_kind !== 'verification' || stage.executor !== 'deterministic_cdp'))
      return 'State observation requires a deterministic_cdp verification stage.';
    if (stage.stage_kind === 'action') {
      const plan = stage.executor_config;
      const match = plan?.page_match as Record<string, unknown> | undefined;
      if (
        !match ||
        ![match.url_contains, match.title_contains].some(
          (value) => typeof value === 'string' && value.trim().length > 0,
        )
      ) {
        return `Action stage '${stage.stage_id}' requires an explicit page_match.`;
      }
      const clicks = (plan?.operations as Record<string, unknown>[] | undefined)?.filter(
        (operation) => operation.kind === 'click',
      );
      if ((clicks?.at(-1)?.target as Record<string, unknown> | undefined)?.kind === 'text') {
        return `Action stage '${stage.stage_id}' requires an exact role or selector for its final click.`;
      }
    }
    if (stageIds.has(stage.stage_id)) {
      return `Stage id '${stage.stage_id}' is duplicated.`;
    }
    stageIds.add(stage.stage_id);
    if (stage.stage_kind === 'narrative' && stage.outcome_contract.fields.length > 0) {
      return `Narrative stage '${stage.stage_id}' cannot declare outcome fields.`;
    }
    if (
      (stage.stage_kind === 'verification' || stage.stage_kind === 'action') &&
      stage.outcome_contract.fields.length === 0
    ) {
      return `${stage.stage_kind === 'action' ? 'Action' : 'Verification'} stage '${stage.stage_id}' requires outcome fields.`;
    }
    const outcomeFields = new Map(
      stage.outcome_contract.fields.map((field) => [field.name, field]),
    );
    if (outcomeFields.size !== stage.outcome_contract.fields.length) {
      return `Stage '${stage.stage_id}' has duplicate outcome field names.`;
    }
    if (stage.readiness_policy) {
      if (stage.stage_kind !== 'verification') {
        return `Stage '${stage.stage_id}' readiness policy requires a verification stage.`;
      }
      if (stage.readiness_policy.conditions.length === 0) {
        return `Stage '${stage.stage_id}' readiness policy requires at least one condition.`;
      }
      for (const condition of stage.readiness_policy.conditions) {
        const outcomeField = outcomeFields.get(condition.field_name);
        if (
          !outcomeField ||
          outcomeField.required === false ||
          outcomeField.value_type === 'string' ||
          !condition.allowed_values.length ||
          !condition.allowed_values.every((value) =>
            isCompatibleReadinessValue(value, outcomeField),
          )
        ) {
          return `Stage '${stage.stage_id}' has an invalid readiness condition for '${condition.field_name}'.`;
        }
      }
    }
  }

  for (let index = 0; index < stages.length; index += 1) {
    const stage = stages[index];
    if (stage.stage_kind === 'verification' && stage.executor === 'deterministic_cdp') {
      const fields = stage.outcome_contract.fields;
      const conditions = stage.readiness_policy?.conditions;
      if (index === stages.length - 1 || !stages.slice(0, index).some(prior => prior.executor !== 'deterministic_cdp'))
        return 'State observation requires an earlier Browser Use stage and a later stage.';
      if (fields.length !== 1 || fields[0].name !== 'state_status' || fields[0].value_type !== 'enum' || fields[0].required === false ||
          [...(fields[0].enum_values ?? [])].sort().join(',') !== 'blocked,not_ready,ready,unknown' ||
          conditions?.length !== 1 || conditions[0].field_name !== 'state_status' || conditions[0].allowed_values.length !== 1 || conditions[0].allowed_values[0] !== 'ready' || stage.result_completion != null)
        return 'State observation requires the fixed state_status result and ready-only gate, without result completion.';
    }
    if (stage.stage_kind === 'structured_extraction') {
      if (!stages.slice(0, index).some((prior) => prior.executor !== 'deterministic_cdp'))
        return 'Structured extraction requires an earlier Browser Use stage.';
      if (stage.outcome_contract.fields.length > 0 || stage.result_completion)
        return 'Structured extraction cannot declare scalar outcomes or result completion.';
      if (index !== stages.length - 1)
        return 'Structured extraction must be the final stage; only one capture is supported.';
    }
    if (stage.stage_kind === 'action') {
      if (index === 0 || stages[index + 1]?.stage_kind !== 'post_action_observation') {
        return `Action stage '${stage.stage_id}' requires a preceding Browser Use stage and following post-action observer.`;
      }
      const plan = stage.executor_config as Record<string, unknown>;
      const operationResultKeys = new Set(
        (plan.operations as Array<Record<string, unknown>>)
          .filter((operation) => MUTATING_OPERATION_KINDS.has(String(operation.kind)))
          .map((operation) => operation.result_key)
          .filter((resultKey): resultKey is string => typeof resultKey === 'string'),
      );
      for (const outcomeField of stage.outcome_contract.fields.filter(
        (field) => field.required !== false,
      )) {
        if (outcomeField.value_type !== 'boolean' || !operationResultKeys.has(outcomeField.name)) {
          return `Action stage '${stage.stage_id}' required outcomes must be boolean mutating-operation result keys.`;
        }
      }
    }
    if (stage.stage_kind === 'deterministic_interaction') {
      if (index === stages.length - 1) {
        return `Deterministic interaction stage '${stage.stage_id}' requires a later flow stage.`;
      }
      if (
        !stages.slice(0, index).some((priorStage) => priorStage.executor !== 'deterministic_cdp')
      ) {
        return `Deterministic interaction stage '${stage.stage_id}' requires an earlier Browser Use stage.`;
      }
      const plan = stage.executor_config as Record<string, unknown>;
      const operationsByResultKey = new Map<string, Array<Record<string, unknown>>>();
      for (const operation of plan.operations as Array<Record<string, unknown>>) {
        if (
          DETERMINISTIC_INTERACTION_RESULT_KINDS.has(String(operation.kind)) &&
          typeof operation.result_key === 'string'
        ) {
          operationsByResultKey.set(operation.result_key, [
            ...(operationsByResultKey.get(operation.result_key) ?? []),
            operation,
          ]);
        }
      }
      const missingResultKeys = stage.outcome_contract.fields
        .filter((field) => field.required !== false && !operationsByResultKey.has(field.name))
        .map((field) => field.name);
      if (missingResultKeys.length > 0) {
        return `Deterministic interaction stage '${stage.stage_id}' required outcomes must be deterministic operation result keys.`;
      }
      for (const field of stage.outcome_contract.fields) {
        const operations = operationsByResultKey.get(field.name);
        if (!operations) {
          continue;
        }
        const hasMutatingOperation = operations.some((operation) =>
          MUTATING_OPERATION_KINDS.has(String(operation.kind)),
        );
        const assertionOperations = operations.filter(
          (operation) => operation.kind === 'assert_value_matches',
        );
        const compatible = hasMutatingOperation
          ? field.value_type === 'boolean'
          : field.value_type === 'string' ||
            (field.value_type === 'enum' &&
              [
                'true',
                ...(assertionOperations.some((operation) => operation.on_absent === 'skip')
                  ? ['not_present']
                  : []),
              ].every((value) => field.enum_values?.includes(value)));
        if (!compatible) {
          return `Deterministic interaction stage '${stage.stage_id}' outcome fields must match their deterministic operation result type.`;
        }
      }
    }
    if (stage.stage_kind === 'post_action_observation') {
      if (
        index === 0 ||
        stages[index - 1]?.stage_kind !== 'action' ||
        index !== stages.length - 1
      ) {
        return `Post-action observer stage '${stage.stage_id}' must immediately follow the final action stage.`;
      }
      if (
        stage.outcome_contract.fields.some((field) => PLATFORM_OBSERVER_FIELD_NAMES.has(field.name))
      ) {
        return `Post-action observer stage '${stage.stage_id}' redeclares platform-owned outcome fields.`;
      }
    }
  }
  return null;
};

const isCompatibleReadinessValue = (value: boolean | number | string, field: BrowserStageOutcomeField): boolean => {
  if (field.value_type === 'boolean') {
    return typeof value === 'boolean';
  }
  if (field.value_type === 'integer') {
    return (
      typeof value === 'number' &&
      Number.isInteger(value) &&
      (field.minimum === undefined || value >= field.minimum) &&
      (field.maximum === undefined || value <= field.maximum)
    );
  }
  return typeof value === 'string' && field.enum_values?.includes(value) === true;
};
